import { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function Tags() {
  const { selectedBusiness } = useAuth();
  const bid = selectedBusiness?.id;
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('#1A1A1A');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showContacts, setShowContacts] = useState(null);
  const [tagContacts, setTagContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const PRESET_COLORS = [
    '#1A1A1A','#16A34A','#2563EB','#DC2626',
    '#9333EA','#EA580C','#0891B2','#BE185D',
    '#44FF00','#F59E0B','#6366F1','#10B981',
  ];

  useEffect(() => { if (bid) loadTags(); }, [bid]);

  const loadTags = () => {
    api.get(`/tags?business_id=${bid}`)
      .then(r => setTags(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  };

  const fetchTagContacts = async (tag) => {
    setShowContacts(tag);
    setContactsLoading(true);
    try {
      const r = await api.get(`/tags/${tag.id}/contacts?business_id=${bid}`);
      setTagContacts(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setTagContacts([]); }
    setContactsLoading(false);
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const getContrast = (hex) => {
    const r = parseInt(hex.slice(1,3),16);
    const g = parseInt(hex.slice(3,5),16);
    const b = parseInt(hex.slice(5,7),16);
    return (0.299*r + 0.587*g + 0.114*b)/255 > 0.55 ? '#1a1a1a' : '#ffffff';
  };

  const handleCreate = async () => {
    if (!tagName.trim()) { setError('Tag name is required'); return; }
    setLoading(true);
    try {
      await api.post('/tags', { name: tagName.trim(), color: tagColor, business_id: bid });
      setTagName(''); setTagColor('#1A1A1A'); setError('');
      setShowCreate(false); loadTags();
      showToast('Tag created successfully!');
    } catch(e) {
      setError(e.response?.data?.error || 'Failed to create tag');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tags/${id}?business_id=${bid}`);
      setShowDelete(null); loadTags();
      setSelected(prev => prev.filter(s => s !== id));
      showToast('Tag deleted', 'danger');
    } catch { showToast('Failed to delete', 'danger'); }
  };

  const handleBulkDelete = async () => {
    for (const id of selected) {
      await api.delete(`/tags/${id}?business_id=${bid}`).catch(() => {});
    }
    setSelected([]); loadTags();
    showToast(`${selected.length} tag(s) deleted`, 'danger');
  };

  const toggleSelect = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);

  const filtered = tags.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  const S = {
    page: { padding: '28px 32px', fontFamily: 'DM Sans, sans-serif', minHeight: '100vh', background: '#f6f8fa' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
    title: { fontSize: 22, fontWeight: 700, color: '#111', margin: 0 },
    sub: { fontSize: 15, color: '#9ca3af', marginTop: 4 },
    greenBtn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
    card: { background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', overflow: 'hidden', marginBottom: 20 },
    statsRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', flexWrap: 'wrap', gap: 10 },
    toolbar: { padding: '14px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    searchBox: { flex: 1, maxWidth: 300, padding: '9px 12px 9px 34px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 15, outline: 'none', background: '#f6f8fa' },
    th: { padding: '12px 14px', textAlign: 'left', color: '#777', fontWeight: 600, fontSize: 15, textTransform: 'uppercase', letterSpacing: 0.6, background: '#f9fafb' },
    td: { padding: '14px 14px', borderTop: '1px solid #f0f0f0', fontSize: 14 },
    badge: (color) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, background: color, color: getContrast(color), borderRadius: 6, padding: '4px 10px', fontSize: 15, fontWeight: 500 }),
    deleteBtn: { background: '#f9f9f9', color: '#888', border: '1px solid #e8e8e8', borderRadius: 6, padding: '6px 10px', fontSize: 15, cursor: 'pointer' },
    bulkBtn: { background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 14px', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { background: '#fff', borderRadius: 12, padding: '28px', minWidth: 420, maxWidth: 480, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },
    modalTitle: { fontSize: 18, fontWeight: 600, color: '#111', margin: '0 0 20px' },
    label: { fontSize: 15, fontWeight: 500, color: '#444', marginBottom: 6, display: 'block' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fafafa' },
    modalBtns: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22 },
    cancelBtn: { padding: '10px 20px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: 14, color: '#555' },
    submitBtn: (bg) => ({ padding: '10px 22px', borderRadius: 8, border: 'none', background: bg, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }),
    toast: (type) => ({ position: 'fixed', bottom: 28, right: 28, zIndex: 9999, background: type === 'danger' ? '#dc2626' : '#16a34a', color: '#fff', borderRadius: 10, padding: '12px 22px', fontSize: 14, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }),
  };

  if (!bid) return <div style={{ padding: 24, color: '#9ca3af' }}>Please select a business first.</div>;

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Tags</h1>
          <p style={S.sub}>Organize and manage your contact tags</p>
        </div>
        <button style={S.greenBtn} onClick={() => { setShowCreate(true); setError(''); setTagName(''); setTagColor('#1A1A1A'); }}>
          + Tag
        </button>
      </div>

      {/* Stats */}
      <div style={{ ...S.card }}>
        <div style={S.statsRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 15, color: '#888' }}>Total Tags</span>
            <span style={{ background: '#16a34a', color: '#fff', borderRadius: 20, padding: '2px 12px', fontSize: 15, fontWeight: 600 }}>
              {String(tags.length).padStart(2, '0')}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {tags.slice(0, 6).map(t => (
              <span key={t.id} style={S.badge(t.color)}>{t.name}</span>
            ))}
            {tags.length > 6 && <span style={{ fontSize: 14, color: '#888', alignSelf: 'center' }}>+{tags.length - 6} more</span>}
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={S.card}>
        <div style={S.toolbar}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: 14 }}>&#128269;</span>
            <input style={S.searchBox} placeholder="Search Tags..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {selected.length > 0 && (
            <button style={S.bulkBtn} onClick={handleBulkDelete}>
              Delete Selected ({selected.length})
            </button>
          )}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 48 }}>
                <input type="checkbox"
                  checked={selected.length === filtered.length && filtered.length > 0}
                  onChange={() => selected.length === filtered.length ? setSelected([]) : setSelected(filtered.map(t => t.id))}
                  style={{ cursor: 'pointer', accentColor: '#16a34a' }} />
              </th>
              <th style={S.th}>S.NO</th>
              <th style={S.th}>Tag Name</th>
              <th style={S.th}>Tag Color</th>
              <th style={S.th}>Contacts</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: '48px 20px', textAlign: 'center', color: '#bbb' }}>No tags found. Create your first tag!</td></tr>
            ) : filtered.map((tag, i) => (
              <tr key={tag.id} style={{ background: selected.includes(tag.id) ? '#f0fdf4' : 'transparent' }}>
                <td style={{ ...S.td, width: 48 }}>
                  <input type="checkbox" checked={selected.includes(tag.id)} onChange={() => toggleSelect(tag.id)}
                    style={{ cursor: 'pointer', accentColor: '#16a34a' }} />
                </td>
                <td style={{ ...S.td, color: '#999' }}>{i + 1}.</td>
                <td style={S.td}><span style={S.badge(tag.color)}>{tag.name}</span></td>
                <td style={S.td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 20, height: 20, borderRadius: 5, background: tag.color, border: '1px solid rgba(0,0,0,0.1)' }} />
                    <span style={{ color: '#666', fontSize: 14, fontFamily: 'monospace' }}>{tag.color}</span>
                  </div>
                </td>
                <td style={S.td}>
                  <span onClick={() => fetchTagContacts(tag)} style={{ cursor: 'pointer', background: '#eff6ff', color: '#2563eb', borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 600 }}>
                    👥 {tag.contact_count || 0} contacts
                  </span>
                </td>
                <td style={{ ...S.td, textAlign: 'right' }}>
                  <button style={S.deleteBtn} onClick={() => setShowDelete(tag)}>&#128465;</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div style={S.modal}>
            <h2 style={S.modalTitle}>Create New Tag</h2>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Tag Name</label>
              <input style={S.input} value={tagName} onChange={e => { setTagName(e.target.value); setError(''); }}
                placeholder="Enter Tag Name" onKeyDown={e => e.key === 'Enter' && handleCreate()} />
              {error && <p style={{ color: '#dc2626', fontSize: 14, marginTop: 6 }}>{error}</p>}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Choose Tag Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <input type="color" value={tagColor} onChange={e => setTagColor(e.target.value)}
                  style={{ width: 40, height: 36, border: '1px solid #e0e0e0', borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                <input value={tagColor} onChange={e => setTagColor(e.target.value)}
                  style={{ ...S.input, width: 120, fontFamily: 'monospace' }} />
                {tagName && <span style={S.badge(tagColor)}>{tagName}</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => setTagColor(c)} style={{
                    width: 26, height: 26, borderRadius: 5, background: c, border: 'none',
                    cursor: 'pointer', outline: tagColor === c ? '2px solid #16a34a' : 'none', outlineOffset: 2,
                  }} />
                ))}
              </div>
            </div>
            <div style={S.modalBtns}>
              <button style={S.cancelBtn} onClick={() => setShowCreate(false)}>Cancel</button>
              <button style={S.submitBtn('#16a34a')} onClick={handleCreate} disabled={loading}>
                {loading ? 'Creating...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDelete && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setShowDelete(null)}>
          <div style={S.modal}>
            <h2 style={S.modalTitle}>Delete Tag</h2>
            <p style={{ fontSize: 14, color: '#555', marginBottom: 10 }}>Are you sure you want to delete this tag?</p>
            <span style={S.badge(showDelete.color)}>{showDelete.name}</span>
            <p style={{ color: '#dc2626', fontSize: 14, marginTop: 10 }}>This action cannot be undone.</p>
            <div style={S.modalBtns}>
              <button style={S.cancelBtn} onClick={() => setShowDelete(null)}>Cancel</button>
              <button style={S.submitBtn('#dc2626')} onClick={() => handleDelete(showDelete.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Contacts Modal */}
      {showContacts && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setShowContacts(null)}>
          <div style={{ ...S.modal, minWidth: 500, maxWidth: 600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h2 style={{ ...S.modalTitle, margin: 0 }}>Contacts in Tag</h2>
                <span style={{ ...S.badge(showContacts.color), marginTop: 6, display: 'inline-flex' }}>{showContacts.name}</span>
              </div>
              <button onClick={() => setShowContacts(null)} style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Close</button>
            </div>
            {contactsLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
            ) : tagContacts.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>👥</div>
                <div>No contacts in this tag yet.</div>
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>{tagContacts.length} contacts</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Name</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tagContacts.map((c, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 1 ? '#fafafa' : '#fff' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: '#111827' }}>{c.name || '—'}</td>
                        <td style={{ padding: '10px 14px', color: '#6b7280', fontFamily: 'monospace' }}>{c.phone}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div style={S.toast(toast.type)}>{toast.msg}</div>}
    </div>
  );
}
